// components/EnderecoMarketplacePaper.tsx
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { useMarketplaces } from "../../../../hooks/useMarketplaces";
import { useUFs } from "../../../../../core/hooks/useUFs";
import { useEffect, useState } from "react";
import { Clear, Edit, LocationOnOutlined, Save } from "@mui/icons-material";
import { useCEP } from "../../../../../core/hooks/useCEP";
import { useMascara } from "../../../../../core/hooks/useMascara";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { SecaoConfiguracao } from "../../../../../core/components/SecaoConfiguracao";

interface EnderecoMarketplacePaperProps {
    marketplace: IMarketplace;
}

export const EnderecoMarketplacePaper = ({ marketplace }: EnderecoMarketplacePaperProps) => {
    const { salvar, loading } = useMarketplaces();
    const ufs = useUFs();

    const [edicaoHabilitada, setEdicaoHabilitada] = useState(false);

    const [bairro, setBairro] = useState<string | undefined>(marketplace.configuracao?.endereco?.bairro);
    const [cidade, setCidade] = useState<string | undefined>(marketplace.configuracao?.endereco?.cidade);
    const [uf, setUf] = useState<string | undefined>(marketplace.configuracao?.endereco?.uf);
    const [cep, setCep] = useState<string | undefined>(marketplace.configuracao?.endereco?.cep);
    const [rua, setRua] = useState<string | undefined>(marketplace.configuracao?.endereco?.rua);
    const [numero, setNumero] = useState<string | undefined>(marketplace.configuracao?.endereco?.numero);
    const [complemento, setComplemento] = useState<string | undefined>(marketplace.configuracao?.endereco?.complemento);
    const [observacao, setObservacao] = useState<string | undefined>(marketplace.configuracao?.endereco?.observacao);

    const { data: cepData, loading: cepLoading } = useCEP(cep || '');
    const snackbar = useSnackbar();

    const handleLimpar = async () => {
        if (!edicaoHabilitada) {
            return setEdicaoHabilitada(true);
        }

        setBairro(undefined)
        setCidade(undefined)
        setUf(undefined)
        setCep(undefined)
        setRua(undefined)
        setNumero(undefined)
        setComplemento(undefined)
        setObservacao(undefined)

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                endereco: undefined
            }
        });
        setEdicaoHabilitada(false);
    }

    const handleEdicaoHabilitada = async () => {
        if (!edicaoHabilitada) {
            return setEdicaoHabilitada(true);
        }

        if (!uf) {
            return snackbar("UF inválida", { severity: "error" });
        }

        if (!cidade) {
            return snackbar("Cidade inválida", { severity: "error" });
        }

        if (!rua) {
            return snackbar("Rua inválida", { severity: "error" });
        }

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                endereco: {
                    bairro,
                    cep,
                    cidade,
                    complemento,
                    numero,
                    observacao,
                    rua,
                    uf
                }
            }
        });
        setEdicaoHabilitada(false);
    };

    useEffect(() => {
        if (cepData) {
            setUf(cepData.uf)
            setCidade(cepData.cidade);
            setRua(cepData.rua);
            setBairro(cepData.bairro);
        }
    }, [cepData]);

    const { mascaraCEP } = useMascara();

    return (
        <SecaoConfiguracao
            titulo="Endereço"
            descricao="Endereço de origem usado no cálculo de entrega"
            icone={<LocationOnOutlined />}
            acoes={
                edicaoHabilitada ? (
                    <>
                        <Button onClick={handleLimpar} size="small" variant="outlined" endIcon={<Clear />}>
                            Limpar
                        </Button>
                        <Button onClick={handleEdicaoHabilitada} size="small" variant="contained" endIcon={<Save />}>
                            Salvar
                        </Button>
                    </>
                ) : (
                    <Button onClick={handleEdicaoHabilitada} size="small" variant="outlined" endIcon={<Edit />}>
                        Editar
                    </Button>
                )
            }
        >
            <Box component="form">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="CEP"
                            value={cep ?? ""}
                            size="small"
                            fullWidth
                            onChange={(e) => setCep(mascaraCEP(e.target.value))}
                            disabled={!edicaoHabilitada}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink id="uf-label">UF*</InputLabel>
                            <Select
                                labelId="uf-label"
                                label="UF*"
                                notched
                                displayEmpty
                                value={uf ?? ""}
                                onChange={(e) => setUf(e.target.value as string)}
                                disabled={!edicaoHabilitada}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 200,
                                        },
                                    },
                                }}
                            >
                                {ufs.map((uf, index) => (
                                    <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Cidade*"
                            value={cidade ?? ""}
                            size="small"
                            fullWidth
                            onChange={(e) => setCidade(e.target.value)}
                            disabled={!edicaoHabilitada}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Bairro*"
                            value={bairro ?? ""}
                            size="small"
                            fullWidth
                            onChange={(e) => setBairro(e.target.value)}
                            disabled={!edicaoHabilitada}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            label="Rua*"
                            value={rua ?? ""}
                            size="small"
                            fullWidth
                            onChange={(e) => setRua(e.target.value)}
                            disabled={!edicaoHabilitada}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Número"
                            value={numero ?? ""}
                            size="small"
                            fullWidth
                            onChange={(e) => setNumero(e.target.value)}
                            disabled={!edicaoHabilitada}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Complemento"
                            value={complemento ?? ""}
                            size="small"
                            fullWidth
                            onChange={(e) => setComplemento(e.target.value)}
                            disabled={!edicaoHabilitada}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Observação"
                            value={observacao ?? ""}
                            size="small"
                            fullWidth
                            onChange={(e) => setObservacao(e.target.value)}
                            disabled={!edicaoHabilitada}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </SecaoConfiguracao>
    );
};
